import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/brands/trade-license/mappings';
export const getCrmBrandTradeLicenseMappingsEndpointIdentifier = endpoint;

export type ICrmBrandTradeLicenseMappingData = {
  id: number;
  entityName: string;
  tradeLicenseName: string;
  licenseNumber: string;
  licenseConfidence: number;
  brandId: number | null;
  brandConfidence: number;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
  } | null;
  dedDetail: {
    id: number;
    licenseNo: string;
    businessName: string;
    legalType: string;
  } | null;
};

type TQueryKey = [typeof endpoint, TParam];
type Options = UseQueryOptions<any, AxiosError, any, TQueryKey>;
type ReturnType = UseQueryResult<{
  data: ICrmBrandTradeLicenseMappingData[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}>;

export function useGetCrmBrandTradeLicenseMappingsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, entityName, tradeLicenseName, brandId, licenseNumber, dedBusinessName, isVerified } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
        sort,
      };

      if (entityName) {
        params.entityName = entityName;
      }

      if (tradeLicenseName) {
        params.tradeLicenseName = tradeLicenseName;
      }

      if (brandId) {
        params.brandId = brandId;
      }

      if (licenseNumber) {
        params.licenseNumber = licenseNumber;
      }

      if (dedBusinessName) {
        params.dedBusinessName = dedBusinessName;
      }

      if (isVerified !== undefined && isVerified !== null && isVerified !== '') {
        params.isVerified = isVerified;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        title: 'Error Loading Mappings',
        description: error.response.data.message || 'Failed to load trade license mappings. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        entityName,
        tradeLicenseName,
        brandId,
        licenseNumber,
        dedBusinessName,
        isVerified,
      },
    ] as any,
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
